import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Environment, ENVIRONMENT, ScimIntegration } from '@netfoundry-ui/shared/model';
import { AnimationOptions } from 'ngx-lottie';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { FeatureService, URLS } from '@netfoundry-ui/shared/services';
import { TableHeaderDefaultComponent } from '@netfoundry-ui/feature/data-table';
import { ScimIntegrationCreateFormComponent } from '@netfoundry-ui/feature/form/scim/scim-integration-create-form';
import { ConfirmComponent } from '@netfoundry-ui/ui/confirm';
import _ from 'lodash';
import { ScimIntegrationService } from '@netfoundry-ui/shared/apiv2';
import { CsvDownloadService, TableFilterService } from '@netfoundry-ui/feature/shared-services';
import { Router} from "@angular/router";

@Component({
    selector: 'lib-scim',
    templateUrl: './scim.component.html',
    styleUrls: ['./scim.component.scss'],
    standalone: false
})
export class ScimComponent implements OnInit, OnDestroy {
    displayedItems: any;
    columnDefs: any;
    changedPage: any;
    showNoData: any;
    canCreate = false;
    subscription = new Subscription();
    basePath = URLS.SCIM;
    isLoading: boolean;
    openIndex = -1;

    dialogRef: MatDialogRef<ScimIntegrationCreateFormComponent> | undefined;
    confirmRef: MatDialogRef<ConfirmComponent> | undefined;

    showInfoBubble = false;
    lottieOptions: AnimationOptions = {
        path: '/assets/animations/SmartZiggy.json',
    };
    typeName = 'SCIM Integrations';
    tableId = 'scim-integrations';
    infoBubbleTitle = 'What is a SCIM Integration?';
    infoBubbleText =
        'A SCIM Integration allows you to sync identities from your Identity Provider to Ziti using the SCIM protocol.' +
        ' Create an SCIM Integration to obtain a URL and authentication method that can be used when configuring your IDP as a SCIM client to sync identities into your Zit network';
    
    // TODO - add doc URL when available
    infoBubbleUrl = 'https://docs.openziti.io/docs/learn/core-concepts/security/authentication/external-jwt-signers/';
    hideZiggy = this.environment.hideZiggy;

    constructor(
        public dialogForm: MatDialog,
        private growlerService: GrowlerService,
        public svc: ScimIntegrationService,
        @Inject(ENVIRONMENT) private environment: Environment,
        private featureService: FeatureService,
        public filterService: TableFilterService,
        public csvDownloadService: CsvDownloadService,
        protected router?: Router
    ) {}

    ngOnInit(): void {
        this.svc.init();
        this.canCreate = this.svc.canCreate();

        this.subscription.add(
            this.svc.filteredRecords.pipe().subscribe((result) => {
                this.displayedItems = result || [];
                this.showNoData = this.displayedItems.length === 0 && _.isEmpty(this.svc.columnFilters?.name) && _.isEmpty(this.svc.columnFilters?.integrationKey);
            })
        );

        this.subscription.add(
            this.svc.confirmDelete.subscribe((item: ScimIntegration) => {
                this.openConfirm('delete', item);
            })
        );

        this.initTableColumns();

        this.subscription.add(
            // subscribing to the filter service to handle the change in page
            this.filterService.setPageEvent.subscribe((pageNum) => {
                this.svc.page = pageNum;
                this.svc.pageHasChanged.next(true);
                this.svc.clearSelected();
                this.svc.debouncedRefreshRecords();
            })
        );

        this.subscription.add(
            // subscribing to the filter service to handle search filter changes
            this.filterService.setFilterEvent.subscribe((filterString) => {
                this.svc.filterString = filterString;
                this.svc.filterHasChanged.next(true);
                this.svc.clearSelected();
                this.svc.debouncedRefreshRecords();
            })
        );

        this.showInfoBubble = this.featureService.getShowInfoBubble(this.tableId) ?? true;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    actionRequested(request: any) {
        this.trackMenuAction(request.action, request.item);
        switch (request.action) {
            case 'toggleAll':
                this.svc.toggleAll(this.displayedItems);
                break;
            case 'toggleItem':
                this.svc.toggle(request.item);
                break;
            case 'delete':
                this.svc.confirmAction(request.action, request.item);
                break;
            case 'update':
                this.edit(true, request.item);
                break;
            case 'downloadCsv':
                this.downloadCsv(request.item);
                break;
            default:
                break;
        }
    }

    trackMenuAction(action: string, item: any) {
        if((window as any).gtag) {
            (window as any).gtag('event', `${action}_scim-integration`, {
                scim_integration_name: item?.name,
                scim_integration_id: item?.id,
            });
        }
    }

    edit(addEvent: boolean, integration?: ScimIntegration) {
        if (addEvent) {
            if ((integration?.id && this.svc.canUpdate(integration.id)) || (!integration && this.svc.canCreate())) {
                if (this.svc.networkReady()) {
                    const model = integration ?? new ScimIntegration();
                    model.networkId = this.svc.currentNetwork.getValue().id;

                    this.openEditForm(model);                    
                } else {
                    this.showNetworkNotReadyError();
                }
            }
        }
    }

    hideInfoBubble() {
        this.featureService.setShowInfoBubble(this.tableId, false);
        this.showInfoBubble = false;
    }

    openConfirm(action: string, item?: ScimIntegration) {
        if (action === 'delete') {
            const deletingInfo = this.getDeletingPrompt(item);

            const data = {
                title: 'Delete',
                appendId: 'Router',
                subtitle: deletingInfo.deleteString,
                bulletList: deletingInfo.names,
                icon: 'DescructiveDelete',
                isDestructive: this.svc.enableDeleteProtection,
                action: 'Yes',
                itemName: deletingInfo.names[0].name,
            };
            this.confirmRef = this.dialogForm.open(ConfirmComponent, {
                data: data,
                height: '340px',
                width: '600px',
                autoFocus: false,
            });
            this.confirmRef.afterClosed().subscribe((result) => {
                if (result && result['loggingOut'] === undefined) {
                    this.svc.delete(result).catch((err) => {
                        this.showDeleteFailure(err);
                    });
                }
            });
        }
    }
    
    getDeletingPrompt(item?: ScimIntegration) {
        const names = [];
        if (item) names.push(item);
        else {
            for (const currItem of this.displayedItems) {
                if ((currItem as any).selected) {
                    names.push(currItem);
                }
            }
        }

        const deleteString = 'Are you sure you would like to delete ';

        return {
            deleteString: deleteString,
            names: names,
        };
    }

    initTableColumns() {
        const headerComponentParams = {
            filterType: 'TEXTINPUT',
            columnFilters: this.svc.columnFilters,
        };

        const checkboxCellRenderer = (row) => {
        let html = '';
        if (row?.data?.serviceIdentityId && row?.data?.jwtConnectionId) {
        html = `<div class="ag-cell-wrapper ag-checkbox-cell" role="presentation">
                    <div role="presentation" ref="eCheckbox" class="ag-labeled ag-label-align-right ag-checkbox ag-input-field ag-disabled" disabled="">
                        <div ref="eLabel" class="ag-input-field-label ag-label ag-hidden ag-checkbox-label" aria-hidden="true" role="presentation"></div>
                        <div ref="eWrapper" class="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ag-checked ag-disabled" role="presentation">
                            <input ref="eInput" class="ag-input-field-input ag-checkbox-input" type="checkbox" tabindex="-1" disabled="" aria-label="checked" aria-live="polite">
                        </div>
                    </div>
                </div>`;
        }
        return html;
    }

        this.columnDefs = [
            {
                colId: 'name',
                width: 120,
                minWidth: 100,
                field: 'name',
                tooltipField: 'name',
                sortColumn: this.svc.sort.bind(this.svc),
                sortable: true,
                headerName: 'Name',
                headerComponent: TableHeaderDefaultComponent,
                headerComponentParams,
                onCellClicked: (params: any) => {
                    this.edit(true, params.data);
                },
                resizable: true,
                cellClass: 'nf-cell-vert-align tCol',
                filter: true,
            },
            {
                colId: 'hasAuthenticationToken',
                width: 50,
                minWidth: 40,
                field: 'hasAuthToken',
                tooltipField: 'Was an authentication token generated',
                sortable: false,
                headerName: 'Has Auth Token',
                headerComponent: TableHeaderDefaultComponent,
                headerComponentParams,
                cellRenderer: checkboxCellRenderer,
                onCellClicked: (params: any) => {
                    this.edit(true, params.data);
                },
                resizable: true,
                cellClass: 'nf-cell-vert-align tCol',
                filter: false,
            },
            {
                colId: 'integrationKey',
                width: 250,
                minWidth: 100,
                field: 'integrationKey',
                tooltipField: 'integrationUrl',
                sortColumn: this.svc.sort.bind(this.svc),
                sortable: true,
                headerName: 'Integration URL',
                headerComponent: TableHeaderDefaultComponent,
                headerComponentParams,
                cellRenderer: (params: any) => 
                    `${this.getIntegrationUrl(params.data)}`,  
                onCellClicked: (params: any) => {
                    this.copy(params.data);
                },
                resizable: true,
                cellClass: 'nf-cell-vert-align tCol',
                filter: true,
            },
            {
                colId: 'id',
                width: 120,
                minWidth: 100,
                field: 'id',
                tooltipField: 'id',
                headerName: 'Id',
                onCellClicked: (params: any) => {
                    this.edit(true, params.data);
                },
                headerComponent: TableHeaderDefaultComponent,
                resizable: true,
                hide: true,
                cellClass: 'nf-cell-vert-align tCol',
                filter: true,
            }
        ];
    }

    public openEditForm(integration: ScimIntegration, basePath?: any) {
        if (!integration.id) {
            this.dialogRef = this.dialogForm.open(ScimIntegrationCreateFormComponent, {
                data: { },
            });
            
        } else {
            basePath = basePath ? basePath : this.basePath;
            this.router?.navigateByUrl(`${basePath}/${integration.id}`);
        }
        
    }

    protected downloadCsv(event: any) {
        const columns = [
            'name',
            'networkId',
            'networkGroupId',
            'integrationKey',
            'createdAt',
            'updatedAt',
        ];
        const filename = 'integrations';
        const translateStatus = false;

        const csvItems = [];

        for (const item of this.displayedItems) {
            if (item.selected) {
                csvItems.push(item);
            }
        }

        this.toggleMenu(event, -2);
        this.csvDownloadService.download(filename, csvItems, columns, translateStatus);
    }

    private toggleMenu(event, index) {
        if (event) event.stopPropagation();
        if (index !== this.openIndex) {
            this.openIndex = index;
        } else {
            this.openIndex = -1;
        }
    }

    protected getIntegrationUrl(item: ScimIntegration) {
        return `${this.svc.integrationClientUrl}${item.integrationKey}/scim/v2`;
    }

    protected copy(item: ScimIntegration) {
        navigator.clipboard.writeText(this.getIntegrationUrl(item));
        this.growlerService.show(
            new GrowlerData(
                'success',
                'Success',
                'Integration URL Copied',
                'The URL has been copied to your clipboard'
            )
        );
    }

    private showDeleteFailure(msg: string) {
        this.growlerService.show(new GrowlerData('error', 'Error', 'One or More Integrations failed to delete.', msg));
    }

    private showEditSuccess() {
        this.growlerService.show(
            new GrowlerData('success', 'Success', 'Integration Save Complete', 'The information has been saved')
        );
    }

    private showEditError() {
        this.growlerService.show(
            new GrowlerData('error', 'Error', 'Unable to Save', 'Unable to Save the provided Integration')
        );
    }

    private showNetworkNotReadyError() {
        this.growlerService.show(
            new GrowlerData('error', 'Error', 'Network not ready', 'The network is not yet PROVISIONED')
        );
    }

}

